import React, { useState } from 'react';
import { X, Folder, Calendar, Trash2, Plus, Pencil, Users, LayoutList, Table2 } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { useAuthStore } from '../../store/authStore';
import CollectionMembersModal from './CollectionMembersModal';

type CollectionsViewMode = 'tiles' | 'list';
const COLLECTIONS_VIEW_KEY = 'netbuilder-collections-view';

function readCollectionsView(): CollectionsViewMode {
  try {
    const raw = localStorage.getItem(COLLECTIONS_VIEW_KEY);
    if (raw === 'list' || raw === 'tiles') return raw;
  } catch {
    /* ignore */
  }
  return 'tiles';
}

function CollectionActions({
  createdBy,
  userId,
  onMembers,
  onRename,
  onDelete,
}: {
  createdBy?: string;
  userId?: string;
  onMembers: (e: React.MouseEvent) => void;
  onRename: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const isOwner = !!userId && createdBy === userId;
  return (
    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={onMembers}
        className="relative p-1 hover:bg-slate-500 rounded transition-colors group/tooltip"
      >
        <Users size={14} className="text-emerald-300" />
        <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md border border-slate-600 bg-slate-900 px-2 py-1 text-[11px] text-gray-200 opacity-0 shadow-lg transition-opacity group-hover/tooltip:opacity-100">
          {isOwner ? 'Invite members' : 'View members'}
        </span>
      </button>
      {isOwner && (
        <>
          <button
            type="button"
            onClick={onRename}
            className="relative p-1 hover:bg-slate-500 rounded transition-colors group/tooltip"
          >
            <Pencil size={14} className="text-blue-300" />
            <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md border border-slate-600 bg-slate-900 px-2 py-1 text-[11px] text-gray-200 opacity-0 shadow-lg transition-opacity group-hover/tooltip:opacity-100">
              Rename collection
            </span>
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="relative p-1 hover:bg-red-600 rounded transition-colors group/tooltip"
          >
            <Trash2 size={14} className="text-red-400" />
            <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md border border-slate-600 bg-slate-900 px-2 py-1 text-[11px] text-gray-200 opacity-0 shadow-lg transition-opacity group-hover/tooltip:opacity-100">
              Delete collection
            </span>
          </button>
        </>
      )}
    </div>
  );
}

interface ProjectListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateNew: () => void;
}

const ProjectListModal: React.FC<ProjectListModalProps> = ({ isOpen, onClose, onCreateNew }) => {
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [membersTarget, setMembersTarget] = useState<{ id: string; name: string; canManage: boolean } | null>(null);
  const [viewMode, setViewMode] = useState<CollectionsViewMode>(readCollectionsView);

  const setCollectionsView = (mode: CollectionsViewMode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(COLLECTIONS_VIEW_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  const { user } = useAuthStore();
  const {
    availableProjects,
    currentProject,
    loadProject,
    deleteProject,
    renameProject,
    refreshAvailableProjects,
  } = useProjectStore();

  React.useEffect(() => {
    if (isOpen) {
      refreshAvailableProjects();
    }
  }, [isOpen, refreshAvailableProjects]);

  if (!isOpen) return null;

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleDeleteProject = (projectId: string, projectName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget({ id: projectId, name: projectName });
  };

  const handleLoadProject = (projectId: string) => {
    loadProject(projectId);
    onClose();
  };

  const handleRenameProject = (projectId: string, currentName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenameTarget({ id: projectId, name: currentName });
    setRenameValue(currentName);
  };

  const handleConfirmRename = () => {
    if (!renameTarget) return;
    const trimmedName = renameValue.trim();
    if (!trimmedName || trimmedName === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    renameProject(renameTarget.id, trimmedName);
    setRenameTarget(null);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteProject(deleteTarget.id);
    setDeleteTarget(null);
  };

  const designCountFor = (project: typeof availableProjects[number]) => {
    const openDesigns = currentProject?.id === project.id ? currentProject.designs : undefined;
    if (openDesigns && openDesigns.length > 0) {
      return openDesigns.length;
    }
    if (typeof project.designCount === 'number') return project.designCount;
    return project.designs?.length ?? 0;
  };

  const handleOpenMembers = (
    projectId: string,
    projectName: string,
    createdBy: string | undefined,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setMembersTarget({
      id: projectId,
      name: projectName,
      canManage: !!user?.id && createdBy === user.id,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[130]">
      <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-700 flex-shrink-0">
          <h2 className="text-xl font-semibold text-white">All Collections</h2>
          <div className="flex items-center space-x-3">
            <div
              className="inline-flex items-center rounded border overflow-hidden flex-shrink-0"
              style={{ borderColor: '#334155' }}
              title="How to show collections"
            >
              <button
                type="button"
                onClick={() => setCollectionsView('tiles')}
                className="inline-flex items-center gap-1 h-8 px-2.5 text-xs font-medium transition-colors"
                style={{
                  backgroundColor: viewMode === 'tiles' ? '#1d2535' : 'transparent',
                  color: viewMode === 'tiles' ? '#60a5fa' : '#94a3b8',
                }}
                title="Tiles"
              >
                <Table2 size={12} />
                Tiles
              </button>
              <button
                type="button"
                onClick={() => setCollectionsView('list')}
                className="inline-flex items-center gap-1 h-8 px-2.5 text-xs font-medium transition-colors"
                style={{
                  backgroundColor: viewMode === 'list' ? '#1d2535' : 'transparent',
                  color: viewMode === 'list' ? '#60a5fa' : '#94a3b8',
                  borderLeft: '1px solid #334155',
                }}
                title="List"
              >
                <LayoutList size={12} />
                List
              </button>
            </div>
            <button
              onClick={onCreateNew}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
            >
              <Plus size={16} />
              <span>New Collection</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-md transition-colors text-gray-400"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          {availableProjects.length === 0 ? (
            <div className="text-center py-12">
              <Folder size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-300 mb-2">No Collections Found</h3>
              <p className="text-gray-400 mb-4">Create your first network design collection to get started</p>
              <button
                onClick={onCreateNew}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md transition-colors"
              >
                Create New Collection
              </button>
            </div>
          ) : viewMode === 'list' ? (
            <div className="overflow-hidden rounded border border-slate-700">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '16%' }} />
                </colgroup>
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-700 bg-slate-900/40">
                    <th className="text-left font-medium px-3 py-2">Collection</th>
                    <th className="text-left font-medium px-3 py-2">Customer</th>
                    <th className="text-left font-medium px-3 py-2">Designs</th>
                    <th className="text-left font-medium px-3 py-2">Updated</th>
                    <th className="text-right font-medium px-3 py-2"> </th>
                  </tr>
                </thead>
                <tbody>
                  {availableProjects.map((project) => {
                    const designCount = designCountFor(project);
                    const isOpen = currentProject?.id === project.id;
                    return (
                      <tr
                        key={project.id}
                        onClick={() => handleLoadProject(project.id)}
                        className={`group cursor-pointer border-b border-slate-700/80 last:border-b-0 ${
                          isOpen ? 'bg-blue-900/20' : 'hover:bg-slate-700/70'
                        }`}
                      >
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Folder className="text-blue-400 flex-shrink-0" size={16} />
                            <span className="text-sm font-medium text-white truncate">{project.name}</span>
                            {isOpen && (
                              <span className="flex-shrink-0 text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded">
                                Open
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-300 truncate">
                          {project.customer?.name || '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-400">
                          {designCount}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-400 truncate">
                          {formatDate(project.updatedAt)}
                        </td>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end">
                            <CollectionActions
                              createdBy={project.createdBy}
                              userId={user?.id}
                              onMembers={(e) => handleOpenMembers(project.id, project.name, project.createdBy, e)}
                              onRename={(e) => handleRenameProject(project.id, project.name, e)}
                              onDelete={(e) => handleDeleteProject(project.id, project.name, e)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableProjects.map((project) => {
                const designCount = designCountFor(project);
                return (
                <div
                  key={project.id}
                  onClick={() => handleLoadProject(project.id)}
                  className={`bg-slate-700 rounded-lg p-4 cursor-pointer transition-all hover:bg-slate-600 border-2 group ${
                    currentProject?.id === project.id
                      ? 'border-blue-500 bg-blue-900/20'
                      : 'border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2 min-w-0">
                      <Folder className="text-blue-400 flex-shrink-0" size={20} />
                      <h3 className="font-semibold text-white truncate">{project.name}</h3>
                    </div>
                    <CollectionActions
                      createdBy={project.createdBy}
                      userId={user?.id}
                      onMembers={(e) => handleOpenMembers(project.id, project.name, project.createdBy, e)}
                      onRename={(e) => handleRenameProject(project.id, project.name, e)}
                      onDelete={(e) => handleDeleteProject(project.id, project.name, e)}
                    />
                  </div>

                  <div className="space-y-2 text-sm text-gray-300">
                    {project.customer?.name && (
                      <div>
                        <span className="text-gray-400">Customer:</span> {project.customer.name}
                      </div>
                    )}
                    {project.customer?.address && (
                      <div className="text-xs text-gray-400 truncate">{project.customer.address}</div>
                    )}
                    <div className="flex items-center space-x-4 text-xs text-gray-400">
                      <span>{designCount} design{designCount !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center space-x-1 text-xs text-gray-400">
                      <Calendar size={12} />
                      <span>Updated {formatDate(project.updatedAt)}</span>
                    </div>
                  </div>

                  {currentProject?.id === project.id && (
                    <div className="mt-3 text-xs bg-blue-600 text-white px-2 py-1 rounded">
                      Currently Open
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {membersTarget && (
        <CollectionMembersModal
          isOpen
          collectionId={membersTarget.id}
          collectionName={membersTarget.name}
          canManage={membersTarget.canManage}
          onClose={() => setMembersTarget(null)}
        />
      )}

      {renameTarget && (
        <div className="fixed inset-0 z-[140] bg-black/60 flex items-center justify-center" onClick={() => setRenameTarget(null)}>
          <div className="bg-slate-900 text-white rounded-lg shadow-2xl w-[420px] max-w-[92vw] border border-slate-700" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-700 font-semibold text-sm flex items-center justify-between">
              <span>Rename Collection</span>
              <button className="p-1 hover:bg-slate-800 rounded" onClick={() => setRenameTarget(null)}>✕</button>
            </div>
            <div className="p-4">
              <label className="block text-xs text-slate-400 mb-2">Collection Name</label>
              <input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div className="px-4 py-3 border-t border-slate-700 bg-slate-900 flex justify-end gap-2">
              <button className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600" onClick={() => setRenameTarget(null)}>Cancel</button>
              <button
                className="px-3 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                disabled={!renameValue.trim()}
                onClick={handleConfirmRename}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[140] bg-black/60 flex items-center justify-center" onClick={() => setDeleteTarget(null)}>
          <div className="bg-slate-900 text-white rounded-lg shadow-2xl w-[420px] max-w-[92vw] border border-slate-700" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-700 font-semibold text-sm flex items-center justify-between">
              <span>Delete Collection</span>
              <button className="p-1 hover:bg-slate-800 rounded" onClick={() => setDeleteTarget(null)}>✕</button>
            </div>
            <div className="p-4 text-sm text-gray-300">
              Are you sure you want to delete <span className="font-semibold text-white">{deleteTarget.name}</span>? This action cannot be undone.
            </div>
            <div className="px-4 py-3 border-t border-slate-700 bg-slate-900 flex justify-end gap-2">
              <button className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button
                className="px-3 py-1.5 text-xs rounded bg-red-600 hover:bg-red-700 text-white"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectListModal;



































